import { stringifyLosslessJson } from '@anchorbase/pillar-sdk';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { diagnosticsApi } from '@/lib/pillarApi';
import {
    AwsCredentialsResponse,
    GetCallerIdentityResponse,
    GetSystemInfoResponse,
    GetSesDiagnosticsResponse,
    EnvironmentVariablesResponseSchema,
    AwsCredentialsResponseSchema,
    GetCallerIdentityResponseSchema,
    GetSystemInfoResponseSchema,
    GetSesDiagnosticsResponseSchema,
} from '@anchorbase/pillar-sdk';

const IdentityVerificationSchema = z.record(
    z.string(),
    z.object({
        VerificationStatus: z.string().optional(),
        VerificationToken: z.string().optional(),
    })
);

function DiagnosticsContent() {
    const {
        data: envVars,
        isLoading: envLoading,
        error: envError,
    } = useQuery<Record<string, string>>({
        queryKey: ['diagnostics', 'env'],
        queryFn: async () => {
            const response = await diagnosticsApi.getEnvironmentVariables();
            return EnvironmentVariablesResponseSchema.parse(response);
        },
    });

    const {
        data: awsCredentials,
        isLoading: credsLoading,
        error: credsError,
    } = useQuery<AwsCredentialsResponse>({
        queryKey: ['diagnostics', 'aws-credentials'],
        queryFn: async () => {
            const response = await diagnosticsApi.getAwsCredentials();
            return AwsCredentialsResponseSchema.parse(response);
        },
    });

    const {
        data: callerIdentity,
        isLoading: identityLoading,
        error: identityError,
    } = useQuery<GetCallerIdentityResponse>({
        queryKey: ['diagnostics', 'aws-caller-identity'],
        queryFn: async () => {
            const response = await diagnosticsApi.getCallerIdentity();
            return GetCallerIdentityResponseSchema.parse(response);
        },
    });

    const {
        data: systemInfo,
        isLoading: systemLoading,
        error: systemError,
    } = useQuery<GetSystemInfoResponse>({
        queryKey: ['diagnostics', 'system-info'],
        queryFn: async () => {
            const response = await diagnosticsApi.getSystemInfo();
            return GetSystemInfoResponseSchema.parse(response);
        },
    });

    const {
        data: sesDiagnostics,
        isLoading: sesLoading,
        error: sesError,
    } = useQuery<GetSesDiagnosticsResponse>({
        queryKey: ['diagnostics', 'ses'],
        queryFn: async () => {
            const response = await diagnosticsApi.getSesDiagnostics();
            return GetSesDiagnosticsResponseSchema.parse(response);
        },
    });

    return (
        <div>
            <h1 className="text-3xl font-bold mb-8">Diagnostics</h1>

            <div className="space-y-6">
                {/* Environment Variables Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>Environment Variables</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {envLoading && <p>Loading environment variables...</p>}
                        {envError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {envError instanceof Error
                                        ? envError.message
                                        : 'Failed to load environment variables'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {envVars && (
                            <div className="space-y-2">
                                <div className="max-h-96 overflow-y-auto">
                                    <pre className="text-xs bg-gray-50 p-4 rounded border">
                                        {stringifyLosslessJson(envVars, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* AWS Credentials Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>AWS Credentials (from ECS Metadata)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {credsLoading && <p>Loading AWS credentials...</p>}
                        {credsError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {credsError instanceof Error
                                        ? credsError.message
                                        : 'Failed to load AWS credentials'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {awsCredentials && (
                            <div className="space-y-2">
                                {awsCredentials.error ? (
                                    <Alert variant="destructive">
                                        <AlertDescription>{awsCredentials.error}</AlertDescription>
                                    </Alert>
                                ) : (
                                    <div className="max-h-96 overflow-y-auto">
                                        <pre className="text-xs bg-gray-50 p-4 rounded border">
                                            {stringifyLosslessJson(awsCredentials, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* AWS Caller Identity Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>AWS Caller Identity (STS GetCallerIdentity)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {identityLoading && <p>Loading caller identity...</p>}
                        {identityError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {identityError instanceof Error
                                        ? identityError.message
                                        : 'Failed to load caller identity'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {callerIdentity && (
                            <div className="space-y-2">
                                {callerIdentity.error ? (
                                    <Alert variant="destructive">
                                        <AlertDescription>{callerIdentity.error}</AlertDescription>
                                    </Alert>
                                ) : (
                                    <div className="space-y-2">
                                        <div>
                                            <span className="font-semibold">Account:</span>{' '}
                                            {callerIdentity.Account || 'N/A'}
                                        </div>
                                        <div>
                                            <span className="font-semibold">ARN:</span>{' '}
                                            {callerIdentity.Arn || 'N/A'}
                                        </div>
                                        <div>
                                            <span className="font-semibold">User ID:</span>{' '}
                                            {callerIdentity.UserId || 'N/A'}
                                        </div>
                                        <div className="mt-4 max-h-96 overflow-y-auto">
                                            <pre className="text-xs bg-gray-50 p-4 rounded border">
                                                {stringifyLosslessJson(callerIdentity, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* System Info Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>System Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {systemLoading && <p>Loading system information...</p>}
                        {systemError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {systemError instanceof Error
                                        ? systemError.message
                                        : 'Failed to load system information'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {systemInfo && (
                            <div className="space-y-2">
                                <div>
                                    <span className="font-semibold">Node Version:</span>{' '}
                                    {systemInfo.nodeVersion}
                                </div>
                                <div>
                                    <span className="font-semibold">Platform:</span>{' '}
                                    {systemInfo.platform}
                                </div>
                                <div>
                                    <span className="font-semibold">Uptime:</span>{' '}
                                    {systemInfo.uptimeFormatted}
                                </div>
                                <div className="mt-4">
                                    <span className="font-semibold">Memory Usage:</span>
                                    <div className="mt-2 space-y-1 text-sm">
                                        <div>
                                            <span className="font-medium">RSS:</span>{' '}
                                            {systemInfo.memoryUsageFormatted.rss}
                                        </div>
                                        <div>
                                            <span className="font-medium">Heap Total:</span>{' '}
                                            {systemInfo.memoryUsageFormatted.heapTotal}
                                        </div>
                                        <div>
                                            <span className="font-medium">Heap Used:</span>{' '}
                                            {systemInfo.memoryUsageFormatted.heapUsed}
                                        </div>
                                        <div>
                                            <span className="font-medium">External:</span>{' '}
                                            {systemInfo.memoryUsageFormatted.external}
                                        </div>
                                        <div>
                                            <span className="font-medium">Array Buffers:</span>{' '}
                                            {systemInfo.memoryUsageFormatted.arrayBuffers}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 max-h-96 overflow-y-auto">
                                    <pre className="text-xs bg-gray-50 p-4 rounded border">
                                        {stringifyLosslessJson(systemInfo, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* SES Diagnostics Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>SES Diagnostics</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {sesLoading && <p>Loading SES diagnostics...</p>}
                        {sesError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {sesError instanceof Error
                                        ? sesError.message
                                        : 'Failed to load SES diagnostics'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {sesDiagnostics && (
                            <div className="space-y-4">
                                {/* Send Quota Section */}
                                <div>
                                    <h3 className="font-semibold mb-2">Send Quota</h3>
                                    {sesDiagnostics.sendQuotaError ? (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                {sesDiagnostics.sendQuotaError}
                                            </AlertDescription>
                                        </Alert>
                                    ) : sesDiagnostics.sendQuota ? (
                                        <div className="space-y-1 text-sm">
                                            <div>
                                                <span className="font-medium">
                                                    Max 24 Hour Send:
                                                </span>{' '}
                                                {sesDiagnostics.sendQuota.Max24HourSend?.toLocaleString() ||
                                                    'N/A'}
                                            </div>
                                            <div>
                                                <span className="font-medium">Max Send Rate:</span>{' '}
                                                {sesDiagnostics.sendQuota.MaxSendRate?.toLocaleString() ||
                                                    'N/A'}{' '}
                                                emails/second
                                            </div>
                                            <div>
                                                <span className="font-medium">
                                                    Sent Last 24 Hours:
                                                </span>{' '}
                                                {sesDiagnostics.sendQuota.SentLast24Hours?.toLocaleString() ||
                                                    'N/A'}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            No quota data available
                                        </p>
                                    )}
                                </div>

                                {/* Identity Verification Section */}
                                <div>
                                    <h3 className="font-semibold mb-2">Identity Verification</h3>
                                    {sesDiagnostics.identityVerificationError ? (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                {sesDiagnostics.identityVerificationError}
                                            </AlertDescription>
                                        </Alert>
                                    ) : (
                                        (() => {
                                            const parseResult =
                                                IdentityVerificationSchema.safeParse(
                                                    sesDiagnostics.identityVerification
                                                );
                                            if (
                                                parseResult.success &&
                                                Object.keys(parseResult.data).length > 0
                                            ) {
                                                return (
                                                    <div className="space-y-2">
                                                        {Object.keys(parseResult.data).map(
                                                            (identity) => {
                                                                const attributes =
                                                                    parseResult.data[identity];
                                                                if (!attributes) {
                                                                    return null;
                                                                }
                                                                return (
                                                                    <div
                                                                        key={identity}
                                                                        className="text-sm"
                                                                    >
                                                                        <div className="font-medium">
                                                                            {identity}
                                                                        </div>
                                                                        <div className="ml-4 space-y-1 text-muted-foreground">
                                                                            <div>
                                                                                <span className="font-medium">
                                                                                    Status:
                                                                                </span>{' '}
                                                                                {attributes.VerificationStatus ||
                                                                                    'N/A'}
                                                                            </div>
                                                                            {attributes.VerificationToken && (
                                                                                <div>
                                                                                    <span className="font-medium">
                                                                                        Token:
                                                                                    </span>{' '}
                                                                                    <code className="text-xs">
                                                                                        {
                                                                                            attributes.VerificationToken
                                                                                        }
                                                                                    </code>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                );
                                            }
                                            return (
                                                <p className="text-sm text-muted-foreground">
                                                    No identity verification data available
                                                </p>
                                            );
                                        })()
                                    )}
                                </div>

                                {/* Raw JSON */}
                                <div className="mt-4 max-h-96 overflow-y-auto">
                                    <pre className="text-xs bg-gray-50 p-4 rounded border">
                                        {stringifyLosslessJson(sesDiagnostics, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default function DiagnosticsPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <DiagnosticsContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
